import { inject, injectable } from "@launchtray/tsyringe-async";
import { ForceRotateFactory, GameFactory, InjectableFactories, LoseFactory, MenuFactory, PauseFactory, VictoryFactory } from "../../../composition/InjectableFactories";
import { PromiseCompletionSource, SceneManager } from "scene-manager";
import { VictorySceneResult } from "../../popups/victory/VictorySceneResult";
import { LoseSceneResult } from "../../popups/lose/LoseSceneResult";
import { PauseSceneResult } from "../../popups/pause/PauseSceneResult";
import { LoseType } from "../../popups/lose/LoseType";
import { BoosterType } from "../../../common/models/data/BoosterType";
import { PlayerModel } from "../../../common/models/PlayerModel";
import { BoxReward } from "../components/3d/gamefield/box/BoxReward";
import { GameModel } from "../models/GameModel";
import { LevelModel } from "../../../common/models/LevelModel";
import { LoseSceneOptions } from "../../popups/lose/LoseSceneOptions";
import { GameViewModel } from "../models/GameViewModel";
import { TutorialModel } from "../../../common/models/TutorialModel";
import { GameSceneOptions } from "../GameSceneOptions";
import { IGameDistributionSdk } from "../../../gamedistribution/interfaces/IGameDistributionSdk";
import { InjectableInterfaces } from "../../../composition/InjectableInterfaces";
import { TickerModel } from "../../../common/models/TickerModel";
import { GameState } from "../models/data/GameState";

@injectable()
export class GameController {
    public constructor(
        @inject(InjectableInterfaces.IGameDistributionSdk) private readonly gdSdk: IGameDistributionSdk,
        private readonly tickerModel: TickerModel,
        private readonly playerModel: PlayerModel,
        private readonly gameModel: GameModel,     
        private readonly gameViewModel: GameViewModel,
        private readonly tutorialModel: TutorialModel,
        private readonly levelModel: LevelModel,
        private readonly sceneManager: SceneManager,
        @inject(InjectableFactories.GameFactory) private readonly gameFactory: GameFactory,
        @inject(InjectableFactories.PauseFactory) private readonly pauseFactory: PauseFactory,
        @inject(InjectableFactories.VictoryFactory) private readonly victoryFactory: VictoryFactory,
        @inject(InjectableFactories.LoseFactory) private readonly loseFactory: LoseFactory,
        @inject(InjectableFactories.MenuFactory) private readonly menuFactory: MenuFactory,
        @inject(InjectableFactories.ForceRotateFactory) private readonly forceRotateFactory: ForceRotateFactory
    ) {
    }

    public start(): void {
        this.gameModel.startLevel();
    }

    public setBoxReward(reward: BoxReward, index: number = -1): void {
        if (reward.booster != null) {
            this.gameViewModel.spawnBoosterOnScreen(reward.booster.type, reward.booster.count, index, reward);
        }
        if (reward.coins != null) {
            this.gameViewModel.spawnCoinOnScreen(reward.coins, index, reward);
        }
        if (reward.heal != null) {
            this.gameViewModel.spawnHealthOnScreen(reward.heal, index, reward);
        }
        if (reward.weapon != null) {
            this.gameViewModel.spawnWeaponOnScreen(reward.weapon.type, reward.weapon.ammo, index, reward);
        }
    }

    public applyDoubleBoxReward(): BoxReward {
        return this.playerModel.applyDoubleBoxReward();
    }

    public setDoubleBoxReward(value: BoxReward): void {
        this.playerModel.setDoubleBoxReward(value);
    }

    public clearBoxReward(): void {
        this.playerModel.clearDoubleBoxReward();
    }

    public addBooster(type: BoosterType, count: number = 1): void {
        this.playerModel.addBooster(type, count);
    }

    public subtractBooster(type: BoosterType): void {
        this.playerModel.subtractBooster(type);
    }

    public addCoins(amount: number): void {
        this.playerModel.addCoins(amount);
    }

    public addCharacterHealth(value: number): void {
        this.playerModel.applyHealth.emit(value);
    }

    public async showPausePopup(operationControls: PromiseCompletionSource<void>, currentSceneOptions: GameSceneOptions): Promise<void> {
        this.tickerModel.onPauseRequest();
        if (this.gameModel.state.value === GameState.Playing) {
            const scene = await this.pauseFactory();
            const result = await this.sceneManager.showViewableAsync(scene);
            switch (result) {
                case PauseSceneResult.Home:
                    await this.showMenu(operationControls);
                    break;
                case PauseSceneResult.Restart:
                    await this.reinitializeGame(operationControls, currentSceneOptions);
                    break;
                case PauseSceneResult.None:
                    break;
            }
        }

        this.tickerModel.onResumeRequest();

    }

    public async showVictoryPopup(operationControls: PromiseCompletionSource<void>, nextSceneOptions: GameSceneOptions): Promise<void> {
        this.playerModel.addCoins(this.levelModel.levelConfiguration.coinsReward);
        const scene = await this.victoryFactory({ coinsReward: this.levelModel.levelConfiguration.coinsReward, savedVegetables: this.gameModel.savedVegetables, totalVegetables: this.levelModel.levelConfiguration.vegetables });
        this.playerModel.completeLevel();
        this.gameModel.endLevel();
        const result = await this.sceneManager.showViewableAsync(scene);
        switch (result) {
            case VictorySceneResult.Home: {
                await this.showMenu(operationControls);
                break;
            }
            case VictorySceneResult.NextLevel: {
                await this.reinitializeGame(operationControls, nextSceneOptions);
                break;
            }
        }
    }

    public async showLosePopup(operationControls: PromiseCompletionSource<void>, currentSceneOptions: GameSceneOptions): Promise<void> {
        let sceneOptions: LoseSceneOptions;
        if (this.gameModel.isVegetablesGone) {
            sceneOptions = {
                type: LoseType.NoVegetablesLeft,
                vegetablesForAd: this.gameModel.vegetablesForContinue
            };
        } else {
            sceneOptions = {
                type: LoseType.Dead
            };
        }
        const scene = await this.loseFactory(sceneOptions);
        const result = await this.sceneManager.showViewableAsync(scene);
        switch (result) {
            case LoseSceneResult.Home:
                this.gameModel.endLevel();
                await this.showMenu(operationControls);
                break;
            case LoseSceneResult.Continue:
                if (sceneOptions.type === LoseType.Dead) {
                    this.playerModel.resurrect();
                }
                this.gameModel.handleCharacterResurrect();
                this.gameModel.continueLevel(sceneOptions.type);
                break;
            case LoseSceneResult.Restart:
                await this.reinitializeGame(operationControls, currentSceneOptions);
                break;
        }
    }

    private async showMenu(operationControls: PromiseCompletionSource<void>): Promise<void> {
        const scene = await this.menuFactory();
        operationControls.complete();
        this.sceneManager.showViewableAsync(scene);
    }

    public async showAd(): Promise<boolean> {
        return await this.gdSdk.showRewardedAd();
    }

    public async showForceRotatePopup(): Promise<void> {
        const scene = await this.forceRotateFactory();
        await this.sceneManager.showViewableAsync(scene);
    }

    private async reinitializeGame(operationControls: PromiseCompletionSource<void>, options: GameSceneOptions): Promise<void> {
        operationControls.complete();
        const scene = await this.gameFactory(options);
        this.sceneManager.showViewableAsync(scene);
    }

    public handleCharacterDie(): void {
        this.gameModel.handleCharacterDie();
    }

    public handleEnemySpawn(): void {
        this.gameModel.handleEnemySpawn();
    }

    public handleEnemyDespawn(): void {
        this.gameModel.handleEnemyDespawn();
    }

    public handleMoleDie(index: number): void {
        this.gameViewModel.spawnCoinOnScreen(5, index, null);
    }

    public handleVegetableEaten(): void {
        this.gameModel.handleVegetableEaten();
    }

    public completeTutorial(): void {
        this.tutorialModel.completeMoleShootTutorial();
    }
}